<template>
    <div class="autoupdater">
        <v-dialog
            v-model="update_available_dialog"
            persistent
            max-width="520"
        >
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    Dostępna aktualizacja
                    <v-spacer></v-spacer>
                    <v-btn
                        icon="mdi-close"
                        variant="text"
                        @click="update_available_dialog = false"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    Korzystasz ze starej wersji aplikacji. Kliknij poniżej, aby pobrać najnowszą
                    wersję zawierającą nowe funkcjonalności i&nbsp;poprawki błędów.
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        color="green"
                        @click="updateApp"
                        class="ml-auto"
                    >
                        Aktualizuj
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="update_dialog"
            persistent
            max-width="480"
        >
            <v-card>
                <v-card-title> Aktualizowanie aplikacji </v-card-title>
                <v-card-text>
                    <template v-if="!update_completed">
                        Trwa pobieranie nowej wersji aplikacji...
                    </template>
                    <div v-if="update_completed">
                        <div class="text-center mb-4">Proszę czekać...</div>
                        <div class="d-flex justify-center mb-4">
                            <v-progress-circular
                                color="primary"
                                size="32"
                                width="4"
                                indeterminate
                                class="mx-auto"
                            />
                        </div>
                    </div>
                    <div
                        class="d-flex align-center mt-4 mb-4"
                        v-else
                    >
                        <v-progress-linear
                            :value="update_dialog_progress"
                            class="px-0 mt-2"
                            style="flex-shrink: 2"
                        />
                        <span
                            style="width: 48px"
                            class="text-right primary--text"
                        >
                            {{ update_dialog_progress }}%
                        </span>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { lt, valid as isValidSemverVersion } from "semver";
import { Stopwatch, waitForMs } from "@/shared/utils/waiters";
import { LOCAL_STORAGE_KEY } from "@/constants";
import { appIsFocused } from "@/shared/utils/app-state";
import { emitter } from "@/services/emitter.service";

let remote_document: null | Document = null;

/*###################
### INNER HELPERS ###
###################*/
async function fetchHTML(path: string) {
    const R = await fetch(path);
    const HTML = await R.text();
    const PARSER = new DOMParser();
    return PARSER.parseFromString(HTML, "text/html");
}
function getAppVersionFromHTML(doc: Document) {
    const RV_TAG = doc.querySelector('meta[name="app-version"]');
    let v = "0.0.0";
    if (RV_TAG) {
        const TV = RV_TAG.getAttribute("content");
        if (TV && isValidSemverVersion(TV)) {
            v = TV;
        }
    }
    return v;
}

/*#######################
### PERFORMING UPDATE ###
#######################*/
const performing_update = ref<boolean>(false);
const update_completed = ref<boolean>(false);
let update_start_time = new Stopwatch();

const update_dialog = ref<boolean>(false);
const update_dialog_progress = ref<number>(0);
const update_dialog_assets_count = ref<number>(0);
const update_dialog_assets_count_loaded = ref<number>(0);

function getAssets() {
    if (!remote_document) return [];

    const ASSETS = [...remote_document.querySelectorAll("link[href], script[src]")];

    return ASSETS.filter(el => {
        const ATTR = el.getAttribute(el.tagName === "LINK" ? "href" : "src");
        return (
            ATTR && ATTR.startsWith("/static") && (ATTR.endsWith(".css") || ATTR.endsWith(".js"))
        );
    })
        .map(el => {
            return {
                path: el.getAttribute(el.tagName === "LINK" ? "href" : "src"),
                tag: el.tagName === "LINK" ? "link" : "script"
            };
        })
        .filter((a, index, self) => index === self.findIndex(b => b.path === a.path));
}

async function handleUpdateProgress() {
    if (update_dialog_assets_count_loaded.value === update_dialog_assets_count.value) {
        // Finished
        await waitForMs(600);
        await update_start_time.waitUntil(1300);
        update_dialog_progress.value = 100;
        await waitForMs(800);
        update_completed.value = true;
        window.location.reload();
    } else {
        // In Progress
        update_dialog_progress.value += Math.floor(80 / update_dialog_assets_count.value);
    }
}
function reportAssetUpdateProgress() {
    update_dialog_assets_count_loaded.value++;
    handleUpdateProgress();
}

async function updateApp() {
    if (performing_update.value) return;
    performing_update.value = true;
    update_start_time = new Stopwatch();

    update_available_dialog.value = false;
    update_dialog.value = true;
    update_dialog_progress.value = 0;

    // 1. Na początku ustawiamy na 20%
    await waitForMs(250);
    update_dialog_progress.value = 20;

    // 2. Zbieramy wszystkie assety *.js oraz *.css
    const ASSETS = getAssets();
    update_dialog_assets_count.value = ASSETS.length;

    // 3. Pobieramy assety
    ASSETS.forEach(a => {
        let el;

        if (a.tag === "link") {
            el = document.createElement("link");
            el.rel = "stylesheet";
            if (a.path) el.href = a.path;
        } else if (a.tag === "script") {
            el = document.createElement("script");
            if (a.path) el.src = a.path;
        }

        if (!el) return;

        document.head.appendChild(el);

        el.onload = () => {
            document.head.removeChild(el);
            reportAssetUpdateProgress();
        };

        el.onerror = () => {
            document.head.removeChild(el);
            reportAssetUpdateProgress();
        };
    });
    if (ASSETS.length === 0) {
        handleUpdateProgress();
    }
}

/*#####################################
### CHECKING IF UPDATE IS AVAILABLE ###
#####################################*/
const update_available_dialog = ref<boolean>(false);

async function checkForUpdates() {
    if (performing_update.value) return;

    // 1. Odstęp min. 30min pomiędzy sprawdzeniami
    const LUCD = localStorage.getItem(LOCAL_STORAGE_KEY.AUTOUPDATER_LAST_CHECK_DATE);
    if (LUCD && /^[1-9][0-9]{0,24}$/.test(LUCD)) {
        const PLUCD = parseInt(LUCD);
        if (!isNaN(PLUCD) && Date.now() - PLUCD < 30 * 60 * 1000) return;
    }

    // 2. Sprawdzenie wersji
    remote_document = await fetchHTML("/?v=" + Date.now());
    const REMOTE_VERSION = await getAppVersionFromHTML(remote_document);
    if (lt(import.meta.env.VITE_NPM_PACKAGE_VERSION, REMOTE_VERSION)) {
        update_available_dialog.value = true;
    }

    // 3. Raportowanie czasu sprawdzenia
    localStorage.setItem(LOCAL_STORAGE_KEY.AUTOUPDATER_LAST_CHECK_DATE, Date.now().toString());
}

/*############################
### BROWSER REFOCUS EVENTS ###
############################*/
function onBrowserActiveStateChange(is_active: boolean) {
    if (is_active === true) {
        checkForUpdates();
    }
}

/*#####################
### LIFECYCLE HOOKS ###
#####################*/
let events_mounted = false;
onMounted(async () => {
    await waitForMs(3000);

    if (await appIsFocused()) {
        checkForUpdates();
    }

    emitter.on("Browser::activestatechange", onBrowserActiveStateChange);
    events_mounted = true;
});
onUnmounted(() => {
    if (events_mounted) {
        emitter.off("Browser::activestatechange", onBrowserActiveStateChange);
    }
});
</script>
